import { Controller } from "react-hook-form";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/inputs/TextInput";
import { Button } from "../../../components/button";
import { useCallback, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import CheckboxInput from "../../../components/inputs/CheckboxInput";
import { emailConfigCheckboxItems } from "../../../constants/common";
import { useGetEmailConfigs, useGetFinalPermitApprovalRecipients } from "../../../hooks/common/query";
import {
  useTestEmail,
  useUpdateEmailConfig,
} from "../../../hooks/common/mutation";
import PasswordVisibilityCard from "../../../components/card/PasswordVisibilityCard";
import { EmailConfigSchema } from "../../../schemas/emailConfigSchema";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import { UpdateEmailConfigBody } from "../../../hooks/common/interface";

interface FormData {
  senderEmail: string;
  senderPassword?: string | null;
  server: string;
  port: string;
  testEmail?: string;
  enableSsl?: boolean;
  enableVisitRequestSubmitEmailToVisitor?: boolean;
  enableVisitApprovalEmail?: boolean;
  enableExternalCancelEmailToVisitor?: boolean;
  enableExternalCancelEmailToHost?: boolean;
  enableVisitRejectedEmailToVisitor?: boolean;
  enableVisitRejectedEmailToHost?: boolean;
  enableVisitApprovedScheduledEmailToVisitor?: boolean;
  enableVisitApprovedScheduledEmailToHost?: boolean;
  enableDevicesRejectedEmailToVisitor?: boolean;
  enableDevicesRejectedEmailToHost?: boolean;
  enableExternalRegistrationEmailToVisitor?: boolean;
  enableExternalPasswordResetEmailToVisitor?: boolean;
  enableCheckInEmail?: boolean;
  enableCheckOutEmail?: boolean;
  enableOTPEmail?: boolean;
  enableDirectStaffApprovalEmailToSecurityUsers?: boolean;
  enableDirectStaffApprovalEmailToSecurityManagers?: boolean;
  enableDirectStaffRejectionEmailToSecurityUsers?: boolean;
  enableDirectStaffRejectionEmailToSecurityManagers?: boolean;
  finalPermitApprovalRecipients?: string;
}

const EmailConfig = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue
  } = useFormWithSchema(EmailConfigSchema);

  const { i18n } = useTranslation();

  const {
    mutateAsync: UpdateEmailConfig,
    isPending: isUpdateEmailConfigLoading,
  } = useUpdateEmailConfig();

  const { mutateAsync: TestEmail, isPending: isTestEmailLoading } =
    useTestEmail();

  const { data: emailConfigs } = useGetEmailConfigs();

  const [configItems, setConfigItems] = useState<
    {
      id: number;
      name: string;
      titleEn: string;
      titleAr: string;
      checked?: boolean;
    }[]
  >([]);

  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  const handleTogglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!emailConfigs) return;

    reset({
      port: emailConfigs.port,
      senderEmail: emailConfigs.adminEmail,
      senderPassword: emailConfigs.adminPassword,
      server: emailConfigs.host,
    });

    const itemsWithChecked = emailConfigCheckboxItems.map((item) => ({
      ...item,
      checked: !!emailConfigs[item.name],
    }));
    setConfigItems(itemsWithChecked);

    // setValue("finalPermitApprovalRecipients", emailConfigs.permitApprovedRecipientEmails || "");
  }, [emailConfigs, reset, setValue]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const body: UpdateEmailConfigBody = {
          adminEmail: data.senderEmail,
          ...(data.senderPassword && { adminPassword: data.senderPassword }),
          host: data.server,
          port: data.port,
          permitApprovedRecipientEmails: data.finalPermitApprovalRecipients || "",
        };

        configItems.forEach((item) => {
          (body as any)[item.name] = item.checked;
        });

        await UpdateEmailConfig(body);
        toast.success(t("emailConfigUpdated"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error updating email config", error);
      }
    },
    [UpdateEmailConfig, configItems, t]
  );

  const onTestEmail = useCallback(
    async (data: { testEmail?: string }) => {
      try {
        if (!data.testEmail) {
          toast.error(t("testEmailRequired"));
          return;
        }

        const body = { s_Email: data.testEmail };
        await TestEmail(body);
        toast.success(t("emailSent"));
      } catch (error) {
        toast.error(t("emailSendError"));
        console.error("Error sending test email", error);
      }
    },
    [TestEmail, t]
  );

  const onClear = useCallback(() => {
    reset({
      port: "",
      senderEmail: "",
      senderPassword: "",
      server: "",
    });

    setConfigItems((prev) => prev.map((item) => ({ ...item, checked: false })));
  }, [reset]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("emailConfig")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-8 bg-white p-4 rounded-xl  shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]"
        >
          <div className=" grid sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
            <Controller
              name="senderEmail"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("senderEmail")}
                  onChange={onChange}
                  value={value || ""}
                  error={errors?.[name]?.message}
                  required
                />
              )}
            />
            <div className="relative flex flex-col gap-1">
              <PasswordVisibilityCard
                isPasswordVisible={isPasswordVisible}
                handleTogglePasswordVisibility={handleTogglePasswordVisibility}
              />

              <Controller
                name="senderPassword"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("senderPassword")}
                    type={isPasswordVisible ? "text" : "password"}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                  />
                )}
              />
            </div>

            <Controller
              name="server"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("server")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                );
              }}
            />

            <Controller
              name="port"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("port")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                );
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            {configItems.map((item) => (
              <CheckboxInput
                key={item.id}
                id={item.id.toString()}
                label={i18n.language === "en" ? item.titleEn : item.titleAr}
                checked={item.checked}
                setChecked={() => {
                  setConfigItems((prev) =>
                    prev.map((i) =>
                      i.id === item.id ? { ...i, checked: !i.checked } : i
                    )
                  );
                }}
              />
            ))}

            {/* <div className="mt-3">
              <Controller
                name="finalPermitApprovalRecipients"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <TextInput
                      label={t("finalPermitApprovalRecipients")}
                      onChange={onChange}
                      value={value || ""}
                      error={errors?.[name]?.message}
                    />
                  );
                }}
              />
            </div> */}
          </div>

          <div className="flex items-center gap-2">
            <Button isLoading={isUpdateEmailConfigLoading}>{t("save")}</Button>
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>

          <div className="flex flex-col gap-4">
            <Controller
              name="testEmail"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("testEmail")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                  />
                );
              }}
            />

            <Button
              type="button"
              isLoading={isTestEmailLoading}
              onClick={handleSubmit(async (data) => onTestEmail(data))}
              className="max-w-max bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("sendTestEmail")}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EmailConfig;
